import React, { useContext } from 'react';
import { TableColumnType, ListingEntry } from 'components';
import { Text, Space } from '@bb/elements-web';
import { useHistory } from 'react-router-dom';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { formatTimeStr, getLocalTimeZone } from 'utils';
import { CommonInnerTableProps, ManagedAccessTableData } from '../types';
import { StatusEntry } from '../../../components';
import { buildTableComponents } from './tableUtils';
import { StyledInnerTable, StyledMobileColumn } from './components';

/** The ManagedAccessTable component displays a table in the Managed Access section of the showings page. */
const ManagedAccessTable: React.FC<
  CommonInnerTableProps<ManagedAccessTableData>
> = ({ tableKey, showings }) => {
  const history = useHistory();
  const { md } = useBreakpoint();
  const { auth } = useContext(AuthContext);

  /** Renders the time entry for a managed access item. */
  const renderTimeEntry = (
    _: unknown,
    record: ManagedAccessTableData[number],
  ) => {
    const timeStr = `${formatTimeStr(record.startDateTime)} - ${formatTimeStr(record.endDateTime)}`;
    return (
      <Space align="center" size={12}>
        <Text>{timeStr}</Text>
      </Space>
    );
  };

  /** Renders the address entry for a managed access item. */
  const renderAddressEntry = (
    _: unknown,
    record: ManagedAccessTableData[number],
  ) => {
    const address = record.listing
      ? `${record.listing.line1}${record.listing.line2 ? ` ${record.listing.line2}` : ''}`
      : 'Address unavailable';
    return <Text>{address}</Text>;
  };

  /** For mobile view, we combine the time and address into a single column to save space. */
  const renderMobile = (_: unknown, record: ManagedAccessTableData[number]) => (
    <StyledMobileColumn>
      {renderTimeEntry(_, record)}
      {renderAddressEntry(_, record)}
    </StyledMobileColumn>
  );

  /** Renders the listing entry for a managed access item. */
  const renderListing = (
    _: unknown,
    record: ManagedAccessTableData[number],
  ) => (
    <ListingEntry
      listingId={record.listing?.id}
      time={record.startDateTime}
      timeEnd={record.endDateTime}
      timezone={getLocalTimeZone(auth)}
      line1={record.listing?.line1}
      line2={record.listing?.line2}
      thumbnail={record.listing?.thumbnail}
      status="confirm"
      data-cy="managed-access-listing-entry"
    />
  );

  /** Renders the status entry for a managed access item. This will always read as "Confirmed". */
  const renderStatusEntry = () => (
    <StatusEntry
      isBuyer={false}
      displayStatus="Confirmed"
      type="MANAGED_ACCESS"
    />
  );

  const columns: TableColumnType<ManagedAccessTableData[number]>[] = md
    ? [
        {
          key: 'thumbnail',
          dataIndex: '',
          width: 275,
          render: renderListing,
        },
        {
          key: 'status',
          dataIndex: '',
          render: renderStatusEntry,
        },
      ]
    : [
        {
          key: 'mobile',
          dataIndex: '',
          render: renderMobile,
        },
      ];

  return (
    <StyledInnerTable
      components={buildTableComponents({ md })}
      key={tableKey}
      columns={columns}
      rowKey={record => record.kdpId}
      showHeader={false}
      dataSource={showings}
      pagination={false}
      data-cy="managed-access-table"
      onRow={record => ({
        // When a row is clicked, navigate to the managed access detail page for that KDP ID
        onClick: () => {
          history.push(`/my_business/managed_access/${record.kdpId}`);
        },
      })}
    />
  );
};

export default ManagedAccessTable;
